/**
 * useEntityList — dual-backed list-of-entities hook.
 *
 * Replaces the localStorage-only `usePersistentMockState` for keys that
 * correspond to known InstantDB entities (see entityNames.ts).
 *
 * Same signature as the old hook:
 *   const [rows, setRows] = useEntityList<MockDealer[]>("dealers", seedDealers);
 *
 * Routing (decided ONCE per component instance based on stable inputs):
 *   - VITE_INSTANT_APP_ID is set + storeKey is a known DB entity + array
 *     initialValue → InstantDB backend.
 *   - Otherwise → legacy localStorage / demoStore.
 *
 * Both inputs (env var + storeKey) are stable for the lifetime of any given
 * component instance, so the hook always executes the same path and the
 * Rules of Hooks are satisfied. The static analyser may flag the
 * conditional call as suspicious; it's a false positive given the
 * stability constraints.
 *
 * On first read from InstantDB, the data set may be empty. The hook does
 * NOT auto-seed from `initialValue` — call `seedFromLocalStorage()` from
 * lib/db/migration.ts to bootstrap the database explicitly.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { id as instantId } from "@instantdb/react";

import { demoStore } from "../demoStore";
import {
  DB_ENTITY_NAMES,
  isDbEntityName,
  type DbEntityName,
} from "./entityNames";
import { encodeEntity, decodeEntities } from "./codec";
import { diffArrays } from "./diff";
import { bulkSetIdMappings, lookupUuid, setIdMapping } from "./idMap";
import { getDb, isInstantDbEnabled } from "./client";

const IS_MOCK = import.meta.env.VITE_USE_MOCK_DATA === "true";

type EntityWithId = { _id: string; id?: string } & Record<string, unknown>;

/* ──────────────────────────────────────────────────────────────────────
 * Public hook
 * ────────────────────────────────────────────────────────────────────── */

export function useEntityList<T>(
  storeKey: string,
  initialValue: T,
): [T, Dispatch<SetStateAction<T>>] {
  const useInstant =
    isInstantDbEnabled() &&
    isDbEntityName(storeKey) &&
    Array.isArray(initialValue);

  // The choice between local/remote is stable per component instance
  // (env var + storeKey + initialValue-shape never change mid-session).
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useInstant
    ? (useInstantBackend<EntityWithId>(
        storeKey as DbEntityName,
        initialValue as unknown as EntityWithId[],
      ) as unknown as [T, Dispatch<SetStateAction<T>>])
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useLocalStorageBackend<T>(storeKey, initialValue);
}

/* ──────────────────────────────────────────────────────────────────────
 * Backend A — legacy localStorage path (demoStore)
 * ────────────────────────────────────────────────────────────────────── */

function useLocalStorageBackend<T>(
  storeKey: string,
  initialValue: T,
): [T, Dispatch<SetStateAction<T>>] {
  const [state, setStateRaw] = useState<T>(() => {
    if (!IS_MOCK) return initialValue;
    return demoStore.get<T>(storeKey, initialValue);
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  const setState: Dispatch<SetStateAction<T>> = useCallback(
    (action) => {
      setStateRaw((prev) => {
        const next =
          typeof action === "function"
            ? (action as (prev: T) => T)(prev)
            : action;
        if (IS_MOCK) {
          demoStore.set(storeKey, next);
        }
        return next;
      });
    },
    [storeKey],
  );

  return [state, setState];
}

/* ──────────────────────────────────────────────────────────────────────
 * Backend B — InstantDB
 * ────────────────────────────────────────────────────────────────────── */

function useInstantBackend<TItem extends EntityWithId>(
  entityName: DbEntityName,
  _initialValue: TItem[],
): [TItem[], Dispatch<SetStateAction<TItem[]>>] {
  // The parent hook (useEntityList) only calls this when isInstantDbEnabled()
  // returns true, which guarantees getDb() returns a live client. We use a
  // non-null assertion here to avoid an early-return-before-hooks pattern
  // that would trip the rules-of-hooks ESLint rule. If the invariant is
  // ever broken (init() fails synchronously despite a valid app id), the
  // first usage of `db.xxx` below throws a clear TypeError.
  const db = getDb()!;

  const query = useMemo(
    () => ({ [entityName]: {} }) as Record<string, unknown>,
    [entityName],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const queryResult = (db.useQuery as any)(query) as {
    data?: Record<string, unknown[]>;
    error?: { message: string };
  };
  const data = queryResult?.data;
  const error = queryResult?.error;

  useEffect(() => {
    if (error) {
      console.error(
        `[useEntityList] InstantDB query for "${entityName}" failed:`,
        error,
      );
    }
  }, [entityName, error]);

  const rawRows = useMemo<TItem[]>(() => {
    const arr = data?.[entityName];
    if (!Array.isArray(arr)) return [];
    return decodeEntities<TItem>(
      entityName,
      arr as ReadonlyArray<Record<string, unknown>>,
    );
  }, [data, entityName]);

  // Refresh the id map after every server-side read
  useEffect(() => {
    if (rawRows.length === 0) return;
    bulkSetIdMappings(
      entityName,
      rawRows.map((r) => ({
        _id: String(r._id),
        id: String(r.id ?? ""),
      })),
    );
  }, [entityName, rawRows]);

  // Optimistic overlay — held until the live data lands
  const [overlay, setOverlay] = useState<TItem[] | null>(null);
  useEffect(() => {
    setOverlay(null);
  }, [rawRows]);

  const rows = overlay ?? rawRows;
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const setRows: Dispatch<SetStateAction<TItem[]>> = useCallback(
    (action) => {
      const prev = rowsRef.current;
      const next =
        typeof action === "function"
          ? (action as (p: TItem[]) => TItem[])(prev)
          : action;

      setOverlay(next);

      const { added, updated, removed } = diffArrays(prev, next);
      if (
        added.length === 0 &&
        updated.length === 0 &&
        removed.length === 0
      ) {
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const txs: any[] = [];

      for (const item of added) {
        const uuid = instantId();
        setIdMapping(entityName, item._id, uuid);
        const encoded = encodeEntity(entityName, item);
        delete encoded.id;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        txs.push((db.tx as any)[entityName][uuid].update(encoded));
      }

      for (const item of updated) {
        let uuid = lookupUuid(entityName, item._id);
        if (!uuid) {
          uuid = instantId();
          setIdMapping(entityName, item._id, uuid);
        }
        const encoded = encodeEntity(entityName, item);
        delete encoded.id;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        txs.push((db.tx as any)[entityName][uuid].update(encoded));
      }

      for (const item of removed) {
        const uuid = lookupUuid(entityName, item._id);
        if (!uuid) continue;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        txs.push((db.tx as any)[entityName][uuid].delete());
      }

      if (txs.length === 0) return;

      db.transact(txs).catch((err: unknown) => {
        console.error(
          `[useEntityList] transact failed for "${entityName}":`,
          err,
        );
      });
    },
    [db, entityName],
  );

  return [rows, setRows];
}

/* Re-exports */
export { DB_ENTITY_NAMES };
